import React, { useEffect, useMemo, useState } from 'react'
import { DeleteOutlined, EditOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Descriptions, Space, Table, Tag, Tooltip } from 'antd'
import RegionCoverageSearch from '../RegionCoverageSearch'
import RegionEditor from '../RegionEditor'
import RegionHotelsModal from '../RegionHotelsModal'
import { ErrorBox, HotelLink, codeTags, coverageColumns } from '../CoverageCommon'
import container from './container'

const describeRegion = region =>
    [region.region_name_full || region.region_name, region.region_type, region.country_code].filter(v => v).join(' / ')

const RegionMissing = ({ result, regionId, supplierCode, perms, onCreateFor }) => {
    const lst = result.lst_region
    if (!lst) {
        return (
            <ErrorBox
                result={result}
                description="The region is not in lst_region either, so search cannot reach it and it cannot be mapped."
            />
        )
    }

    return (
        <Alert
            type="info"
            showIcon
            message={`Region ${regionId} exists in lst_region (${describeRegion(lst)}) but has no hotels mapped${
                supplierCode ? ` under source ${supplierCode}` : ''
            }`}
            description="Search reaches a region only through hotel_mapping_v2 rows, so it returns no hotels here until some are mapped."
            action={
                perms.create ? (
                    <Button size="small" type="primary" onClick={() => onCreateFor(regionId)}>
                        Create mapping
                    </Button>
                ) : null
            }
        />
    )
}

const RegionResult = props => {
    const { result, loading, supplierCode, perms, onPage, onAddHotels, onEdit, onDelete, onRemoveHotels } = props
    const [selected, setSelected] = useState([])

    useEffect(() => {
        setSelected([])
    }, [result])

    const hotelColumns = useMemo(() => {
        const total = (result.coverage || []).length
        return [
            { key: 'hotel_id', dataIndex: 'hotel_id', title: 'Hotel ID', render: id => <HotelLink id={id} /> },
            { key: 'name', title: 'Name', render: hotel => (hotel.static && hotel.static.name) || '' },
            { key: 'city', title: 'City', render: hotel => (hotel.static && hotel.static.city) || '' },
            {
                key: 'category',
                title: 'Category',
                width: 100,
                render: hotel => (hotel.static && hotel.static.category) || ''
            },
            {
                key: 'supplier_codes',
                dataIndex: 'supplier_codes',
                title: 'Sources',
                width: 160,
                render: codeTags
            },
            {
                key: 'mapped',
                dataIndex: 'mapped',
                title: 'Mapped catalogs',
                width: 150,
                render: mapped => (total ? `${mapped} / ${total}` : mapped)
            }
        ]
    }, [result])

    const { region } = result
    const lst = region.lst_region
    const withoutProvider = result.hotels_in_region - result.hotels_total

    return (
        <>
            <Card
                title={region.region_name_full || region.region_name || `Region ${region.region_id}`}
                extra={
                    <Space>
                        {perms.edit ? (
                            <Button icon={<EditOutlined />} onClick={() => onEdit(region)}>
                                Edit metadata
                            </Button>
                        ) : null}
                        {perms.edit ? (
                            <Button icon={<PlusOutlined />} onClick={() => onAddHotels(region.region_id)}>
                                Add hotels
                            </Button>
                        ) : null}
                        {perms.delete ? (
                            <Button danger icon={<DeleteOutlined />} onClick={() => onDelete(region)}>
                                {supplierCode ? `Delete ${supplierCode} rows` : 'Delete region'}
                            </Button>
                        ) : null}
                    </Space>
                }>
                <Descriptions size="small" column={3}>
                    <Descriptions.Item label="Region ID">{region.region_id}</Descriptions.Item>
                    <Descriptions.Item label="Type">{region.region_type || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Country">{region.country_code || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Sources">
                        {(region.supplier_codes || []).map(item => (
                            <Tag
                                color={item.supplier_code === supplierCode ? 'blue' : undefined}
                                key={item.supplier_code}>
                                {item.supplier_code}: {item.rows} rows
                            </Tag>
                        ))}
                        {region.metadata_mismatch ? (
                            <Tooltip title={lst ? `lst_region: ${describeRegion(lst)}` : 'Not in lst_region'}>
                                <Tag color="orange" icon={<WarningOutlined />}>
                                    metadata differs from lst_region
                                </Tag>
                            </Tooltip>
                        ) : null}
                    </Descriptions.Item>
                    <Descriptions.Item label="Hotels" span={2}>
                        {result.hotels_total} with a provider row, out of {result.hotels_in_region} in this region
                        {withoutProvider > 0 ? (
                            <Tag color="red" style={{ marginLeft: 8 }}>
                                {withoutProvider} without a provider row, invisible to search
                            </Tag>
                        ) : null}
                    </Descriptions.Item>
                </Descriptions>
                <Table
                    bordered
                    size="small"
                    rowKey="column"
                    dataSource={result.coverage}
                    columns={coverageColumns}
                    pagination={false}
                />
            </Card>
            <br />
            <Card
                title={`Hotels (${result.hotels_total})`}
                extra={
                    perms.edit ? (
                        <Button danger disabled={!selected.length} onClick={() => onRemoveHotels(region, selected)}>
                            Remove selected{selected.length ? ` (${selected.length})` : ''}
                        </Button>
                    ) : null
                }>
                <Table
                    bordered
                    size="small"
                    rowKey="hotel_id"
                    loading={loading}
                    dataSource={result.hotels}
                    columns={hotelColumns}
                    rowSelection={
                        perms.edit ? { selectedRowKeys: selected, onChange: keys => setSelected(keys) } : undefined
                    }
                    expandable={{ expandedRowRender: hotel => codeTags(hotel.columns) }}
                    pagination={{
                        current: result.page,
                        pageSize: result.page_size,
                        total: result.hotels_total,
                        showSizeChanger: true,
                        pageSizeOptions: ['20', '50', '100', '200'],
                        onChange: onPage
                    }}
                />
            </Card>
        </>
    )
}

const RegionCoverage = props => {
    const {
        regionCoverage,
        loading,
        query,
        supplierCode,
        perms,
        handlePage,
        handleCreate,
        handleCreateFor,
        handleAddHotels,
        handleEdit,
        handleDelete,
        handleRemoveHotels
    } = props

    const renderResult = () => {
        if (!regionCoverage) {
            return loading ? <Card size="small" loading /> : null
        }
        if (regionCoverage.success) {
            return (
                <RegionResult
                    result={regionCoverage}
                    loading={loading}
                    supplierCode={supplierCode}
                    perms={perms}
                    onPage={handlePage}
                    onAddHotels={handleAddHotels}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onRemoveHotels={handleRemoveHotels}
                />
            )
        }
        if (regionCoverage.status === 404) {
            return (
                <RegionMissing
                    result={regionCoverage}
                    regionId={query && query.region_id}
                    supplierCode={supplierCode}
                    perms={perms}
                    onCreateFor={handleCreateFor}
                />
            )
        }
        return <ErrorBox result={regionCoverage} />
    }

    return (
        <div>
            <RegionCoverageSearch canCreate={perms.create} onCreate={handleCreate} />
            <br />
            {renderResult()}
            <RegionEditor />
            <RegionHotelsModal />
        </div>
    )
}

export default container(RegionCoverage)
